export const projects = [
    {
        id: 1,
        title: "Useless",
        category: "AI Art",
        year: "2024",
        description: "An ongoing AI project imagining surreal objects through vivid prompts. Each piece explores light, texture, material, and emotion.",
        image: "/images/useless.webp",
        link: "/work/useless",
        externalLink: "https://www.jacobfaulkner.co.uk/work/useless/", // open static project folder
        tags: ["AI", "Generative Art", "3D"],
        aspect: "4:5",
        videoLink: "https://example.com/useless-video",
        customContent: "An ongoing exploration of AI-generated surreal objects that challenge our perception of utility and beauty. Each piece is crafted through carefully engineered prompts that explore the intersection of light, texture, material, and emotion. The project examines what makes an object 'useless' in our consumer-driven society while celebrating the aesthetic potential of the absurd and impractical."
    },
    {
        id: 2,
        title: "Earth Day",
        category: "Interactive Installation",
        year: "2025",
        description: "Interactive webapp presenting 3D scanned models of waste in Bristol's castle park.",
        image: "/images/earth-day/earth-day.jpg",
        link: "/work/earth-day",
        externalLink: "/work/earth-day/", // open static project folder
        tags: ["Interactive", "Lidar Scanning", "Environmental","Documentary"],
        aspect: "5:4",
        videoLink: "https://example.com/earth-day-video",
        customContent: "An environmental awareness documentary that uses advanced 3D scanning technology to capture and document waste materials found in Bristol's Castle Park. The project combines LiDAR scanning with interactive storytelling to create an immersive experience that highlights the environmental impact of consumer waste. Visitors can explore detailed 3D models of discarded objects, learning about their decomposition timelines and environmental consequences through an interactive digital installation."
    },
    {
        id: 3,
        title: "Turbo VR",
        category: "Virtual Reality",
        year: "2023 (ongoing)",
        description: "VR game exploring the boundaries of waste and perception of Turbo Island in a virtual environment.",
        image: "/images/placeholders/turbo-vr.jpg",
        link: "/work/turbo-vr",
        tags: ["VR", "Cultural", "Game Development"],
        aspect: "5:4",
        videoLink: "https://vimeo.com/1034252837",
        customContent: "An ongoing VR experience that recreates the cultural landmark of Turbo Island in Bristol within a virtual environment. The project explores themes of waste, community, and urban culture through an immersive game that allows players to interact with the space in ways impossible in reality. Using photogrammetry and 3D modeling, the virtual Turbo Island becomes a canvas for exploring the boundaries between digital and physical spaces, waste and value, and individual and collective memory."
    },
    {
        id: 4,
        title: "Liquidation — Glastonbury Festival, Shangri-La",
        category: "Festival Installation",
        year: "2024",
        description: "Ordos Studio (Jacob Faulkner) × Knobhead Industries\n\nWindow Installation · Public Art · Material Commentary",
        image: "/images/liquidation/liquidation (5).jpg",
        link: "/work/liquidation",
        tags: ["Installation", "Festival", "Collaboration","3D Modeling"],
        aspect: "16:9",
        images: [
            "/images/liquidation/liquidation (1).jpg",
            "/images/liquidation/liquidation (2).jpg",
            "/images/liquidation/liquidation (3).jpg",
            "/images/liquidation/liquidation (4).jpg",
            "/images/liquidation/liquidation (5).jpg",
            "/images/liquidation/liquidation (6).jpg",
            "/images/liquidation/liquidation (7).jpg",
            "/images/liquidation/liquidation (8).jpg",
            "/images/liquidation/liquidation (9).jpg",
            "/images/liquidation/liquidation (10).jpg",
            "/images/liquidation/liquidation (11).jpg"
        ],
        contentSections: [
            {
                title: "Overview",
                content: `<p>Liquidation was a satirical, street-facing window installation created for Shangri-La at Glastonbury Festival — a zone known for its anti-capitalist storytelling and politically charged visual culture. At its centre was a 30 cm ice sculpture of the pound sterling symbol (£), repeatedly melting and being replaced throughout the festival.</p>

<p>The piece transformed the £ symbol into a fragile, temporary object — a physical metaphor for consumerism, economic collapse, and the relentless erosion of personal assets under late capitalism.</p>`,
                imageIndices: []
            },
            {
                title: "The Brief",
                content: `<p>To create a window installation that literally visualised money melting away, using a recognisable economic symbol placed within an environment themed around everyday financial pressure.</p>`,
                imageIndices: []
            },
            {
                title: "Concept",
                content: `<p>Developed collaboratively with <a href="https://www.instagram.com/knobhead_industries" target="_blank">Knobhead Industries</a>, the installation turned the melting of a frozen £ symbol into a ritualistic performance. As each sculpture dissolved, the work highlighted:</p>

<ul>
<li>the instability of modern financial systems,</li>
<li>the absurdity of the consumer economy,</li>
<li>and the dark humour that defines Shangri-La's visual language.</li>
</ul>

<p>The interior walls were covered with custom wallpaper made from printed receipt paper showing monthly outgoing bills — turning personal economic strain into an immersive set design.</p>`,
                imageIndices: [4, 10] // liquidation (5).jpg and liquidation (11).jpg
            },
            {
                title: "My Role — <a href=\"http://www.jacobfaulkner.co.uk\" target=\"_blank\">Ordos Studio</a> / Jacob Faulkner",
                content: `<ul>
<li>Concept development support, including early visual direction.</li>
<li>3D modelling the oversized £ symbol in Cinema 4D.</li>
<li>3D printing the 30 cm PLA master for mould creation.</li>
<li>Fabrication assistance, ensuring the geometry cast cleanly in ice.</li>
<li>Lighting support, especially for the backlit display.</li>
<li>Design and production of the receipt-paper wallpaper used inside the installation.</li>
</ul>

<p><a href="https://www.instagram.com/knobhead_industries" target="_blank">Knobhead Industries</a> led the core concept, fabrication build, mould-making workflow, installation, and the rotation of the melting sculptures throughout the festival.</p>`,
                imageIndices: [5, 6] // liquidation (6).jpg and liquidation (7).jpg
            },
            {
                title: "Process & Fabrication",
                content: `<h3>1. Digital Sculpt & Master Print</h3>

<ul>
<li>Modeled the pound sterling symbol digitally.</li>
<li>Produced a large-format PLA 3D print to serve as the mould master.</li>
</ul>

<h3>2. Silicone Mould Production</h3>

<ul>
<li>Multiple one-part silicone moulds were created for efficient recasting.</li>
<li>Each mould was filled and frozen externally.</li>
</ul>

<h3>3. Cyclical Melt Performance</h3>

<ul>
<li>Frozen £ symbols lasted 2–3 hours before fully collapsing.</li>
<li>Fresh symbols were rotated in, creating a continuous loop of "asset decay."</li>
</ul>`,
                imageIndices: [3, 1] // liquidation (4).jpg and liquidation (2).jpg
            },
            {
                title: "Installation",
                content: `<ul>
<li>Installed inside a pitched, street-facing window at Shangri-La.</li>
<li>Ice sculpture positioned on a plinth and backlit for maximum visual clarity.</li>
<li>Fans and reflective insulation were used to slow melting.</li>
<li>A custom drainage tray captured meltwater below the piece.</li>
<li>The interior was wrapped in receipt-paper wallpaper, reinforcing the theme of financial exhaustion.</li>
</ul>`,
                imageIndices: [2, 1, 8] // liquidation (3).jpg, liquidation (2).jpg, and liquidation (9).jpg
            },
            {
                title: "Outcome",
                content: `<p>Liquidation operated as a live, self-erasing artwork — a visually striking and darkly comedic meditation on the instability of value. Festival-goers repeatedly returned to witness new £ symbols melt down, making the concept feel alive and continuously unfolding.</p>

<p>The piece successfully combined material performance, social critique, and immersive design, fitting seamlessly within the rebellious ethos of Shangri-La.</p>`,
                imageIndices: [7, 0] // liquidation (8).jpg and liquidation (1).jpg
            }
        ],
        customContent: `<h2>Overview</h2>

<p>Liquidation was a satirical, street-facing window installation created for Shangri-La at Glastonbury Festival — a zone known for its anti-capitalist storytelling and politically charged visual culture. At its centre was a 30 cm ice sculpture of the pound sterling symbol (£), repeatedly melting and being replaced throughout the festival.</p>

<p>The piece transformed the £ symbol into a fragile, temporary object — a physical metaphor for consumerism, economic collapse, and the relentless erosion of personal assets under late capitalism.</p>

<h2>The Brief</h2>

<p>To create a window installation that literally visualised money melting away, using a recognisable economic symbol placed within an environment themed around everyday financial pressure.</p>

<h2>Concept</h2>

<p>Developed collaboratively with <a href="https://www.instagram.com/knobhead_industries" target="_blank">Knobhead Industries</a>, the installation turned the melting of a frozen £ symbol into a ritualistic performance. As each sculpture dissolved, the work highlighted:</p>

<ul>
<li>the instability of modern financial systems,</li>
<li>the absurdity of the consumer economy,</li>
<li>and the dark humour that defines Shangri-La's visual language.</li>
</ul>

<p>The interior walls were covered with custom wallpaper made from printed receipt paper showing monthly outgoing bills — turning personal economic strain into an immersive set design.</p>

<h2>My Role — <a href="http://www.jacobfaulkner.co.uk" target="_blank">Ordos Studio</a> / Jacob Faulkner</h2>

<ul>
<li>Concept development support, including early visual direction.</li>
<li>3D modelling the oversized £ symbol in Cinema 4D.</li>
<li>3D printing the 30 cm PLA master for mould creation.</li>
<li>Fabrication assistance, ensuring the geometry cast cleanly in ice.</li>
<li>Lighting support, especially for the backlit display.</li>
<li>Design and production of the receipt-paper wallpaper used inside the installation.</li>
</ul>

<p><a href="https://www.instagram.com/knobhead_industries" target="_blank">Knobhead Industries</a> led the core concept, fabrication build, mould-making workflow, installation, and the rotation of the melting sculptures throughout the festival.</p>

<h2>Process & Fabrication</h2>

<h3>1. Digital Sculpt & Master Print</h3>

<ul>
<li>Modeled the pound sterling symbol digitally.</li>
<li>Produced a large-format PLA 3D print to serve as the mould master.</li>
</ul>

<h3>2. Silicone Mould Production</h3>

<ul>
<li>Multiple one-part silicone moulds were created for efficient recasting.</li>
<li>Each mould was filled and frozen externally.</li>
</ul>

<h3>3. Cyclical Melt Performance</h3>

<ul>
<li>Frozen £ symbols lasted 2–3 hours before fully collapsing.</li>
<li>Fresh symbols were rotated in, creating a continuous loop of "asset decay."</li>
</ul>

<h2>Installation</h2>

<ul>
<li>Installed inside a pitched, street-facing window at Shangri-La.</li>
<li>Ice sculpture positioned on a plinth and backlit for maximum visual clarity.</li>
<li>Fans and reflective insulation were used to slow melting.</li>
<li>A custom drainage tray captured meltwater below the piece.</li>
<li>The interior was wrapped in receipt-paper wallpaper, reinforcing the theme of financial exhaustion.</li>
</ul>

<h2>Outcome</h2>

<p>Liquidation operated as a live, self-erasing artwork — a visually striking and darkly comedic meditation on the instability of value. Festival-goers repeatedly returned to witness new £ symbols melt down, making the concept feel alive and continuously unfolding.</p>

<p>The piece successfully combined material performance, social critique, and immersive design, fitting seamlessly within the rebellious ethos of Shangri-La.</p>`
    },
    {
        id: 5,
        title: "Depth DJ - Cosmic Junkyard",
        category: "Interactive VJ Installation",
        year: "2023",
        description: "Interactive VJ setup using depth cameras and projection mapping.",
        image: "/images/depth-dj/cosmic-junkyard.jpg",
        link: "/work/depth-dj",
        tags: ["Installation", "VJ", "Projection Mapping"],
        aspect: "5:4",
        customContent: "Commissioned by the Cosmic Junkyard venue at Boomtown festival, I created an interactive performance using TouchDesigner and Resolume. DJs and performers were 3D scanned via an Xbox Kinect, with their visuals dynamically reacting to the music in real time.",
        images: [
            "/images/depth-dj/cosmic-junkyard.jpg",
            "/images/depth-dj/depths.jpg",
            "/images/depth-dj/depths02.jpg",
            "/images/depth-dj/cjy1.jpg"
        ]
    },
    {
        id: 6,
        title: "Backhand Exhibition",
        category: "Exhibition Design",
        year: "2022 (ongoing)",
        description: "Co Creator of Backhand Exhibition.",
        image: "/images/placeholders/backhand-exhibition.jpg",
        link: "/work/backhand-exhibition",
        tags: ["Exhibition", "Art", "Bristol"],
        aspect: "1:1",
        customContent: "Co-creator of the Backhand Exhibition, an ongoing collaborative project that explores themes of resistance, subversion, and alternative narratives in contemporary art. The exhibition serves as a platform for emerging and established artists to showcase work that challenges conventional gallery practices and engages with social and political issues. As co-creator, I contribute to curatorial decisions, exhibition design, and the development of innovative presentation methods that enhance the visitor experience and amplify the artists' messages."
    },
    {
        id: 7,
        title: "Zubieta World",
        category: "Virtual Environment",
        year: "2022",
        description: "Digital world creation collaboration with Zubieta exploring the intersection of 2D illustration and VR.",
        image: "/images/placeholders/zubieta-world.jpg",
        link: "/work/zubieta-world",
        tags: ["VR", "2D Illustration", "Collaboration"],
        aspect: "5:4",
        customContent: "A collaborative digital world creation project with artist Zubieta that bridges the gap between traditional 2D illustration and immersive VR experiences. The project explores how flat, illustrative artwork can be transformed into navigable 3D environments, creating unique spatial experiences that maintain the aesthetic qualities of hand-drawn art while adding depth and interactivity. This collaboration pushes the boundaries of how we experience illustrated narratives in digital spaces."
    },
    {
        id: 8,
        title: "Telegram Production Assistant",
        category: "Creative Technology",
        year: "2025",
        description: "AI-powered Telegram bot for controlling and interacting with my computer remotely.",
        image: "/images/telegram-bot/telegrambot1.jpg",
        link: "/work/telegram-bot",
        tags: ["AI", "Bot", "Assistant"],
        aspect: "1:1",
        customContent: "An AI-powered Telegram bot that serves as a remote production assistant, allowing me to control and interact with my computer from anywhere in the world. The bot integrates with various software applications and system functions, enabling remote file management, software execution, and creative workflow automation. This tool represents the intersection of AI, mobile communication, and creative production workflows, demonstrating how technology can extend creative capabilities beyond physical limitations."
    },
    {
        id: 9,
        title: "Tat Plus Vision",
        category: "Theatre",
        year: "2021",
        description: "This needs changings.",
        image: "/images/placeholders/tat-plus-vision.jpg",
        link: "/work/tat-plus-vision",
        tags: ["Game Development", "Motion Graphics", "Interactive Installation","Theatre"],
        aspect: "5:4",
        customContent: "In collaboration with artist Tat Vision, I created a live video and motion capture performance featuring facial tracking and avatar animation. Using skeletal data from Microsoft Kinect, the project brought a 3D avatar to life on screen in real-time. https://www.glee.co.uk/performer/tat-vision-live/"
    },
    {
        id: 10,
        title: "B-Road Bashers",
        category: "Electronics",
        year: "2024",
        description: "Sensor installation for artist Morgan Tipping.",
        image: "/images/placeholders/b-road-bashers.jpg",
        link: "/work/b-road-bashers",
        tags: ["Electronics", "Audio", "Installation"],
        aspect: "1:1",
        customContent: "A collaborative sensor installation created for artist Morgan Tipping that explores the intersection of sound, movement, and technology. The installation uses custom-built electronic sensors to detect and respond to environmental changes, creating an interactive audio-visual experience that transforms the gallery space into a responsive environment. This project demonstrates the potential of DIY electronics in contemporary art practice."
    },
    {
        id: 12,
        title: "Imbue - Freddo",
        category: "3D Rendering",
        year: "2020",
        description: "Interactive brand experience for Freddo chocolate using motion graphics and touch interfaces.",
        image: "/images/imbue-freddo/Freddo Renders_0001_freddo_3_0127.jpg",
        link: "/work/imbue-freddo",
        tags: ["3D Modeling", "Fabrication", "Contempory Art"],
        aspect: "1:1",
        images: [
            "/images/imbue-freddo/Freddo Renders_0000_freddo_0127.jpg",
            "/images/imbue-freddo/Freddo Renders_0001_freddo_3_0127.jpg",
            "/images/imbue-freddo/Freddo Renders_0002_freddo_2_0127.jpg"
        ],
        customContent: "An interactive brand experience created for Freddo chocolate that combines 3D modeling, motion graphics, and touch interfaces to create an engaging consumer experience. The project explores how digital technology can enhance brand interaction, using tactile interfaces and responsive visuals to create memorable moments of connection between consumers and the brand."
    },
    {
        id: 13,
        title: "Foka wolf - Gold Bar",
        category: "Fabrication",
        year: "2020",
        description: "Digital street art project exploring themes of value and materialism through augmented reality.",
        image: "/images/foka-wolf-gold-bar/Gold-bar_0001_Goldbar 1.jpg",
        link: "/work/foka-wolf-gold-bar",
        tags: ["Street Art", "3D Modeling", "Fabrication"],
        aspect: "5:4",
        images: [
            "/images/foka-wolf-gold-bar/Gold-bar_0000_Goldbar 2.jpg",
            "/images/foka-wolf-gold-bar/Gold-bar_0001_Goldbar 1.jpg"
        ],
        customContent: "A digital street art project that explores themes of value, materialism, and economic inequality through augmented reality. The project uses 3D modeling and fabrication techniques to create physical objects that serve as triggers for AR experiences, blending digital and physical art practices to create new forms of public engagement with social and economic issues."
    },
    {
        id: 14,
        title: "Choosing Children",
        category: "Performance",
        year: "2022",
        description: "Interactive theatre exploring reproductive choices through immersive storytelling techniques.",
        image: "/images/choosing-children/choosing-children.jpg",
        video: "/images/choosing-children/choosing-children1.webm",
        link: "/work/choosing-children",
        tags: ["Theatre", "Motion Graphics", "Performance"],
        aspect: "5:4",
        customContent: "I was commissioned to create motion graphics using video data to produce morphing effects of actors. These visuals were projected behind the performers and cued with video mapping software at the end of each chapter, adding dynamic transitions to the performance. https://benfredericks.co.uk/project/choosing-children/"
    }
    ,
    {
        id: 21,
        title: "Angkor Wat - Unesco Heritage Site 3D Scanning",
        category: "3D Scanning",
        year: "2025",
        description: "3D scanning project documenting the UNESCO World Heritage Site of Angkor Wat, capturing detailed architectural elements and textures for digital preservation and study.",
        image: "/images/angkor-wat/Angkor wat scans__0002_Photo 23-09-2025, 13 52 02.jpg",
        link: "/work/angkor-wat",
        tags: ["3D Scanning", "Heritage Site", "3D Modeling"],
        aspect: "5:4",
        images: [
            "/images/angkor-wat/Angkor wat scans__0000_Photo 23-09-2025, 13 53 28.jpg",
            "/images/angkor-wat/Angkor wat scans__0001_Photo 23-09-2025, 13 52 46.jpg",
            "/images/angkor-wat/Angkor wat scans__0002_Photo 23-09-2025, 13 52 02.jpg",
            "/images/angkor-wat/Angkor wat scans__0003_Photo 23-09-2025, 13 51 30.jpg",
            "/images/angkor-wat/Angkor wat scans__0004_Photo 23-09-2025, 13 50 43.jpg",
            "/images/angkor-wat/Angkor wat scans__0005_Photo 23-09-2025, 13 55 01.jpg"
        ],
        videoLink: "https://example.com/angkor-wat-video",
        customContent: "A comprehensive 3D scanning project documenting the UNESCO World Heritage Site of Angkor Wat in Cambodia. Using advanced photogrammetry and 3D scanning techniques, this project captures the intricate architectural details, textures, and spatial relationships of this ancient temple complex. The resulting digital models serve as a permanent record for preservation, research, and educational purposes, ensuring that future generations can study and experience this cultural treasure even as the physical structure faces environmental and time-based challenges."
    },
    {
        id: 22,
        title: "Christ in Bag",
        category: "3D Modeling",
        year: "2023",
        description: "3D modeling and installation project exploring themes of faith, materiality, and contemporary art through sculptural representation.",
        image: "/images/christ-in-bag/Christ in bag__0002_Christ in bag_New_Take.jpg",
        link: "/work/christ-in-bag",
        tags: ["3D", "Installation", "Collaboration"],
        aspect: "4:5",
        images: [
            "/images/christ-in-bag/Christ in bag__0000_Christ in bag_New_Take_2.jpg",
            "/images/christ-in-bag/Christ in bag__0001_Christ in bag_New_Take_1.jpg",
            "/images/christ-in-bag/Christ in bag__0002_Christ in bag_New_Take.jpg"
        ],
        videoLink: "https://example.com/christ-in-bag-video",
        customContent: "A collaborative 3D modeling and installation project that explores themes of faith, materiality, and contemporary art through sculptural representation. The project examines the intersection of religious iconography and everyday objects, creating thought-provoking installations that challenge traditional notions of sacred and profane. Using advanced 3D modeling techniques, the work creates new forms of spiritual expression that resonate with contemporary audiences."
    },
    {
        id: 24,
        title: "Data Mine - VJ Set",
        category: "VJ Performance",
        year: "2024",
        description: "Live VJ performance for Data Mine at Shambala Festival 2025.",
        image: "/images/data-mine/data-mine.jpg",
        link: "/work/data-mine",
        tags: ["VJ Performance", "Festival", "Motion Graphics"],
        aspect: "16:9",
        customContent: "A VJ performance project that explores the intersection of data visualization and live performance. The project uses real-time data processing and motion graphics to create dynamic visual experiences that respond to both musical input and environmental data. This work demonstrates how digital information can be transformed into compelling visual narratives that engage audiences in new ways."
    },
    {
        id: 25,
        title: "Davinci Kids TV Ident",
        category: "Motion Graphics",
        year: "2024",
        description: "3D logo for Davinci Kids TV.",
        image: "linear-gradient(135deg, #E6FFFB 0%, #D6FFF6 100%)",
        link: "/work/davinci-scan",
        tags: ["3D Scanning", "Motion Graphics", "VFX"],
        aspect: "5:4",
        customContent: "A 3D logo animation created for Davinci Kids TV that combines motion graphics with VFX techniques to create an engaging visual identity. The project demonstrates the potential of 3D scanning and modeling in broadcast design, creating memorable brand moments that capture the attention of young audiences while maintaining professional production values."
    },
    {
        id: 26,
        title: "Grimace - Unhappy Meal",
        category: "3D Modeling",
        year: "2024",
        description: "Project entry placeholder for Grimace.",
        image: "linear-gradient(135deg, #FFE6FB 0%, #FFD6F6 100%)",
        link: "/work/grimace",
        tags: ["3D Modeling", "Fabrication", "Collaboration"],
        aspect: "5:4",
        customContent: "A collaborative 3D modeling and fabrication project that explores themes of consumer culture and childhood nostalgia through the lens of fast food iconography. The project uses 3D modeling and fabrication techniques to create new interpretations of familiar characters and objects, challenging viewers to reconsider their relationship with commercial imagery and cultural symbols."
    },
    
    {
        id: 28,
        title: "Key To Our Hearts",
        category: "3D Modeling",
        year: "2024",
        description: "Project entry placeholder for Shambala Key.",
        image: "/images/shambala-key/shambalaKey (3).jpg",
        link: "/work/shambala-key",
        tags: ["3D Modeling", "Fabrication", "Collaboration"],
        aspect: "1:1",
        hidden: true,
        images: [
            "/images/shambala-key/shambalaKey (1).jpg",
            "/images/shambala-key/shambalaKey (2).jpg",
            "/images/shambala-key/shambalaKey (3).jpg",
            "/images/shambala-key/shambalaKey (4).jpg"
        ],
        customContent: "Commissioned to design and sculpt a bespoke commemorative key for Shambhala Festival, created as a symbolic gift for the organisation's team. Using Cinema 4D and digital sculpting, I developed an ornate, gothic-inspired design that incorporated the festival's logo. Each key was high-fidelity 3D printed in resin, moulded, and cast in bronze, with unique numbering from 1–20. This project required fine attention to detail, translating digital ornamentation into a tangible, highly crafted sculptural edition."
    },
    {
        id: 29,
        title: "Sickboy Landscape",
        category: "3D Modeling",
        year: "2022",
        description: "Recreating artist sketches to large format printing.",
        image: "linear-gradient(135deg, #FFFBE6 0%, #FFF2D6 100%)",
        link: "/work/sickboy-landscape",
        tags: ["3D Modeling", "Rendering", "Collaboration"],
        aspect: "5:4",
        customContent: "A collaborative project with artist Sickboy that transforms hand-drawn sketches into large-format digital prints. The project explores the relationship between traditional drawing techniques and digital reproduction, using 3D modeling and rendering to enhance the original sketches while maintaining their authentic character. This work demonstrates how digital technology can amplify the impact of traditional artistic practices."
    },
    {
        id: 30,
        title: "Tom Davis - Sculture Digitisation",
        category: "3D Scanning",
        year: "2021",
        description: "3D scanning and digitisation project for artist Tom Davis, capturing detailed sculptural works for digital preservation and documentation.",
        image: "/images/tom-davis/_0000_mask_Main2.jpg",
        link: "/work/tom-davis",
        tags: ["3D Scanning", "3D Modeling", "Collaboration"],
        aspect: "4:5",
        images: [
            "/images/tom-davis/_0000_mask_Main2.jpg",
            "/images/tom-davis/_0001_mask_New_Take_12.jpg",
            "/images/tom-davis/_0002_Humunculus_Main2.jpg",
            "/images/tom-davis/_0003_Humunculus_Main.jpg"
        ],
        customContent: "A comprehensive 3D scanning and digitization project for artist Tom Davis, capturing detailed sculptural works for digital preservation and documentation. Using advanced photogrammetry and 3D scanning techniques, this project creates high-fidelity digital models of physical sculptures, ensuring their preservation and enabling new forms of digital interaction and study."
    },
    {
        id: 31,
        title: "Google Dorker Sequencer",
        category: "Creative Technology",
        year: "2025",
        description: "Interactive web tool for generating and sequencing Google search queries.",
        image: "/images/google-dorker-sequencer/Google_Dorker_sequencer.jpg",
        link: "/work/google-dorker-sequencer",
        externalLink: "/work/dorker/index.html",
        tags: ["OSINT", "Creative Coding", "Research Tool"],
        aspect: "5:4",
        customContent: "An interactive web tool for generating and sequencing Google search queries, designed for OSINT (Open Source Intelligence) research and creative exploration. The tool combines creative coding with practical research applications, enabling users to discover hidden information and explore the vast landscape of publicly available data through innovative search techniques."
    },
    {
        id: 32,
        title: "Bangkok Record Store",
        category: "Interactive Installation",
        year: "2025",
        description: "A 3D model of a record store in Bangkok, rendered in real-time using Three.js. Explore the interior and exterior of this detailed architectural model.",
        image: "/images/bangkok-recordstore/bkrs.jpg",
        link: "/work/bangkok-recordstore",
        externalLink: "/work/bangkok-recordstore/",
        tags: ["Interactive", "3D Modeling", "WebGL", "Architecture"],
        aspect: "16:9",
        customContent: "An interactive 3D visualization of a record store in Bangkok, created using Three.js and real-time rendering. This project allows users to explore a detailed architectural model of the store, navigating both interior and exterior spaces. The interactive viewer features customizable lighting controls, smooth camera navigation, and high-fidelity 3D models that capture the unique character and atmosphere of the Bangkok record store."
    },
    {
        id: 33,
        title: "Art Scans",
        category: "3D Scanning",
        year: "2025",
        description: "An interactive 3D viewer documenting exhibitions, art shows, and pop-ups. Each scan captures the spatial experience of these temporary art spaces, preserving them in digital form.",
        image: "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)",
        link: "/work/art-scans",
        externalLink: "/work/art-scans/",
        tags: ["3D Scanning", "Interactive", "Documentation", "WebGL", "Exhibition"],
        aspect: "16:9",
        customContent: "An interactive 3D viewer for documenting exhibitions, art shows, and pop-ups. Each scan captures the spatial experience of these temporary art spaces, preserving them in digital form. The viewer uses Three.js to render 3D models in real-time, allowing users to explore exhibition spaces that would otherwise be lost after the shows end. The project includes metadata tracking for each scan, including exhibition names, dates, participating artists, and locations, creating a digital archive of contemporary art spaces."
    }
];

export const getProjectsByCategory = (category) => {
    return projects.filter(project => project.category === category);
};

export const getProjectsByYear = (year) => {
    return projects.filter(project => project.year === year);
};

export const getProjectsByTag = (tag) => {
    return projects.filter(project => project.tags.includes(tag));
};

// Helper function to convert project title to slug
export const getProjectSlug = (title) => {
    return title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
};

// Helper function to find project by slug
export const getProjectBySlug = (slug) => {
    return projects.find(project => getProjectSlug(project.title) === slug);
};

// Helper function to check if project is external
export const isExternalProject = (project) => {
    return project.externalLink !== null && project.externalLink !== undefined;
}; 